import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Plus, Search, X } from 'lucide-react';
import { apiService } from '../../services/api';

const STATUS_LABELS = { not_started: 'À faire', in_progress: 'En cours', blocked: 'Bloqué' };

// Choisir d'autres objectifs pour aujourd'hui parmi ses objectifs ouverts.
const ObjectivePicker = ({ employeeId, chosenIds, onChoose, onClose }) => {
  const [objectives, setObjectives] = useState(null);
  const [query, setQuery] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    apiService.get(`/objectives?owner_ids=${employeeId}&statuses=not_started,in_progress,blocked&limit=300`)
      .then(setObjectives)
      .catch((err) => setError(err.message));
  }, [employeeId]);

  useEffect(() => {
    const onKey = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return (objectives || [])
      .filter((o) => !chosenIds.has(o.id))
      .filter((o) => !needle || `${o.name} ${o.project_name || ''}`.toLowerCase().includes(needle))
      .sort((a, b) => (a.status === 'in_progress' ? 0 : 1) - (b.status === 'in_progress' ? 0 : 1)
        || String(a.project_name).localeCompare(String(b.project_name)));
  }, [objectives, query, chosenIds]);

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 px-4 py-10" onMouseDown={onClose}>
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl" onMouseDown={(event) => event.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Choisir pour aujourd'hui</h2>
          <button type="button" onClick={onClose} className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100" title="Fermer"><X size={18} /></button>
        </div>
        <div className="px-5 py-3">
          <label className="flex items-center gap-2 rounded-lg border border-slate-200 px-3 py-1.5 focus-within:ring-2 focus-within:ring-indigo-100">
            <Search size={14} className="text-slate-400" />
            <input autoFocus value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher un objectif ou un projet"
              className="flex-1 text-sm outline-none" />
          </label>
        </div>
        <div className="max-h-[60vh] overflow-y-auto px-3 pb-4">
          {error && <p className="px-2 text-sm text-rose-600">{error}</p>}
          {!objectives && !error && (
            <p className="flex items-center gap-2 px-2 py-4 text-sm text-slate-400"><Loader2 size={14} className="animate-spin" /> Chargement…</p>
          )}
          {objectives && visible.length === 0 && <p className="px-2 py-4 text-sm text-slate-400">Aucun autre objectif ouvert.</p>}
          {visible.map((objective) => (
            <button
              key={objective.id}
              type="button"
              onClick={() => onChoose(objective)}
              className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-slate-50"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-800">{objective.name}</p>
                <p className="truncate text-xs text-slate-500">
                  {objective.project_name}{objective.sprint_name ? ` · ${objective.sprint_name}` : ''} · {STATUS_LABELS[objective.status] || objective.status}
                </p>
              </div>
              <span className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-semibold text-indigo-600 opacity-0 group-hover:opacity-100">
                <Plus size={12} /> Aujourd'hui
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ObjectivePicker;
